import React, { useState } from "react";
import {
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  ModalBody,
  Row,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalHeader,
  ModalFooter,
} from "reactstrap";
import { Btn, H4, P } from "../../../../../AbstractElements";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  userService,
  USER_QUERY_KEY,
  USER_VIEW_QUERY_KEY,
} from "../../../../../Services/userService";
import {
  employeeService,
  EMPLOYEE_QUERY_KEY,
} from "../../../../../Services/employeeService";
import { Employee } from "../../../../../Types/Employee/EmployeeModel";
import { toast } from "react-toastify";

const generateSecurePassword = () => {
  const length = 12;
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numbers = "0123456789";
  const symbols = "!@#$%^&*()_+=-";
  const all = lower + upper + numbers + symbols;

  let password = "";
  password += lower[Math.floor(Math.random() * lower.length)];
  password += upper[Math.floor(Math.random() * upper.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];

  for (let i = password.length; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }

  return password
    .split("")
    .sort(() => 0.5 - Math.random())
    .join("");
};

const RegisterUserForm = ({ toggle }: { toggle?: () => void }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [isAgreed, setAgreedChecked] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const queryClient = useQueryClient();

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [formData, setFormData] = useState({
    emp_no: "",
    username: "",
    password: "",
    password_confirmation: "",
  });

  const { data: employees = [], isLoading } = useQuery({
    queryKey: EMPLOYEE_QUERY_KEY,
    queryFn: employeeService.getAllEmployee,
  });

  const registerMutation = useMutation({
    mutationFn: userService.registerUser,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: USER_VIEW_QUERY_KEY });

      setGeneratedPassword(data.password);
      setPasswordModalOpen(true);

      const successMessage = "Account has been registered successfully";
      toast.success(successMessage);
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.errors) {
        const errorMessage = "Employee has an account already.";
        toast.error(errorMessage);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error("Failed to register user. Please check your inputs.");
      }
    },
  });

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedChecked(e.target.checked);
  };

  const handleSelectEmployee = (emp: Employee) => {
    const tempPassword = generateSecurePassword();
    setSelectedEmployee(emp);
    setFormData({
      ...formData,
      emp_no: emp.emp_no,
      username: emp.emp_no,
      password: tempPassword,
      password_confirmation: tempPassword,
    });
    setAgreedChecked(false);
  };

  const handleGeneratePassword = () => {
    const tempPassword = generateSecurePassword();
    setFormData({
      ...formData,
      password: tempPassword,
      password_confirmation: tempPassword,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    registerMutation.mutate(formData);
  };

  const closeModalAndToggleParent = () => {
    setPasswordModalOpen(false);
    if (toggle) toggle();
  };
  const [search, setSearch] = useState("");

  const filteredEmployees = employees.filter(
    (emp: Employee) =>
      emp.employment_status === 2 &&
      `${emp.emp_no} ${emp.first_name} ${emp.last_name}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label className="fw-bold">Step 1: Select Employee</Label>
            <Dropdown
              isOpen={dropdownOpen}
              toggle={() => setDropdownOpen(!dropdownOpen)}
              direction="down"
              className="mb-2 w-100"
            >
              <DropdownToggle
                color="primary"
                caret
                className="w-100 text-start d-flex justify-content-between align-items-center"
              >
                {selectedEmployee
                  ? `${selectedEmployee.emp_no}  (${selectedEmployee.first_name} ${selectedEmployee.last_name})`
                  : "Search / Select Employee..."}
              </DropdownToggle>

              <DropdownMenu
                className="dropdown-block w-100 p-2"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                {/* 🔍 Typeahead Input */}
                <Input
                  type="text"
                  placeholder="Type to search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mb-2"
                  autoFocus
                />

                {isLoading && <DropdownItem disabled>Loading...</DropdownItem>}

                {!isLoading && filteredEmployees.length > 0
                  ? filteredEmployees.map((emp: Employee) => (
                      <DropdownItem
                        key={emp.id}
                        onClick={() => {
                          handleSelectEmployee(emp);
                          setSelectedEmployee(emp);
                          setSearch("");
                          setDropdownOpen(false);
                        }}
                      >
                        <strong>{emp.emp_no}</strong> ({emp.first_name}{" "}
                        {emp.last_name})
                      </DropdownItem>
                    ))
                  : !isLoading && (
                      <DropdownItem disabled>No employees found</DropdownItem>
                    )}
              </DropdownMenu>
            </Dropdown>
          </Col>

          <Col md="12">
            <div className="bg-light p-3 rounded border">
              <Row className="g-2">
                <Col md="12" className="mb-2">
                  <h6 className="sub-title text-muted mb-0">
                    Employee Information (Read Only)
                  </h6>
                </Col>
                <Col md="6">
                  <Label className="text-muted small">First Name</Label>
                  <Input
                    type="text"
                    value={selectedEmployee?.first_name || ""}
                    disabled
                    className="bg-white border-0"
                  />
                </Col>
                <Col md="6">
                  <Label className="text-muted small">Middle Name</Label>
                  <Input
                    type="text"
                    value={selectedEmployee?.middle_name || ""}
                    disabled
                    className="bg-white border-0"
                  />
                </Col>
                <Col md="12">
                  <Label className="text-muted small">Last Name</Label>
                  <Input
                    type="text"
                    value={selectedEmployee?.last_name || ""}
                    disabled
                    className="bg-white border-0"
                  />
                </Col>
                <Col hidden>
                  <Label className="text-muted small">Email Address</Label>
                  <Input
                    type="text"
                    value={selectedEmployee?.email || ""}
                    disabled
                    className="bg-white border-0"
                  />
                </Col>
              </Row>
            </div>
          </Col>

          <Col md="12" className="mt-4">
            <Label className="fw-bold">
              Step 2: Create Account Credentials
            </Label>
          </Col>

          <Col md="12">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              name="username"
              type="text"
              placeholder="Username"
              required
              value={formData.emp_no}
              onChange={handleInputChange}
              readOnly
            />
          </Col>

          <Col md="12">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="text"
              placeholder="Password"
              required
              value={formData.password}
              onChange={handleInputChange}
              readOnly
            />
          </Col>

          <Col hidden>
            <Label htmlFor="password_confirmation">Confirm Password</Label>
            <Input
              id="password_confirmation"
              name="password_confirmation"
              type="text"
              placeholder="Confirm Password"
              required
              value={formData.password_confirmation}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Btn
              color="secondary"
              type="button"
              onClick={handleGeneratePassword}
              className="w-100"
            >
              Generate New Secure Password
            </Btn>
          </Col>

          <Col md="12" className="mt-3">
            <FormGroup check>
              <Input
                id="flexCheckDefault"
                type="checkbox"
                required
                checked={isAgreed}
                onChange={handleCheck}
              />
              <Label htmlFor="flexCheckDefault" check className="text-muted">
                I confirm that I want to register this user account.
              </Label>
            </FormGroup>
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={registerMutation.isPending || !selectedEmployee}
            >
              {registerMutation.isPending ? "Creating Account..." : "Sign Up"}
            </Btn>
          </Col>
        </Row>
      </Form>

      <Modal
        isOpen={passwordModalOpen}
        toggle={closeModalAndToggleParent}
        centered
      >
        <ModalHeader toggle={closeModalAndToggleParent}>
          Account Registered Successfully
        </ModalHeader>
        <ModalBody>
          <P className="text-success fw-bold">
            The new user account has been created!
          </P>
          <P>
            Please provide the following temporary password to the user for
            their first login. They will be prompted to change it immediately.
          </P>

          <div className="bg-light p-3 rounded border text-center">
            <H4 className="text-primary">{generatedPassword}</H4>
          </div>

          <P className="mt-3 text-danger">
            **NOTE:** This password will not be displayed again.
          </P>
        </ModalBody>
        <ModalFooter>
          <Btn color="primary" onClick={closeModalAndToggleParent}>
            Close & Finish
          </Btn>
        </ModalFooter>
      </Modal>
    </ModalBody>
  );
};

export default RegisterUserForm;
